import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signUp, logIn } from '../../auth';
import './style.css'; // We'll add styles in Login.css
import { EyeIcon, EyeSlashIcon } from '../../assets/icons/icons'; // Import your Eye icons
import { trackGoal } from '../../utils/datafast';
import { attributeAffiliateSignup, getAffiliateSignUpMetadata } from '../../utils/affiliate';

export default function Login({ defaultSignUp = false, onAuthSuccess, onAuthNoSession }) {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState(null);
    const [isSigningUp, setIsSigningUp] = useState(!!defaultSignUp); // Toggle between sign-in and sign-up
    const [showPassword, setShowPassword] = useState(false); // State for toggling password visibility
    const [accountCreated, setAccountCreated] = useState(false); // State for showing account created message

    const navigate = useNavigate();

    useEffect(() => {
        setIsSigningUp(!!defaultSignUp);
    }, [defaultSignUp]);

    // Handle Email Sign-In or Sign-Up
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null); // Reset error message

        try {
            if (isSigningUp) {
                if (password !== confirmPassword) {
                    throw new Error("Passwords do not match.");
                }
                // If signing up, create a new account
                const { session } = await signUp(email, password, getAffiliateSignUpMetadata());
                trackGoal("signup", { auth_method: "email" });
                setAccountCreated(true); // Show confirmation message
                if (session && onAuthSuccess) {
                    await attributeAffiliateSignup(session);
                    await onAuthSuccess({ mode: "signup", session });
                } else if (!session && onAuthNoSession) {
                    onAuthNoSession({ mode: "signup" });
                }
            } else {
                // If logging in, proceed with login and redirect
                const { session } = await logIn(email, password);
                trackGoal("login", { auth_method: "email" });
                if (onAuthSuccess) {
                    await onAuthSuccess({ mode: "login", session });
                } else {
                    navigate('/dashboard');
                }
            }
        } catch (error) {
            setError(error.message);
        }
    };

    // Toggle password visibility
    const togglePasswordVisibility = () => {
        setShowPassword((prevState) => !prevState);
    };

    return (
        <div className="auth-card">
        {error && <p className="error-message" role="alert">{error}</p>}
            
            {isSigningUp && accountCreated && (
                <p className="success-message" role="status">Check your email for a confirmation link to complete signup.</p>
            )}

            <form className="auth-form" onSubmit={handleSubmit}>
                <label htmlFor="email">Email</label>
                <input
                    type="email"
                    value={email}
                    id="email"
                    name="email"
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email address"
                    autoComplete="email"
                    required
                    className='login-input'
                />

                <label htmlFor="password">Password</label>
                <div className="password-input-container">
                    <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        id="password"
                        name="password"
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Password"
                        autoComplete={isSigningUp ? 'new-password' : 'current-password'}
                        required
                        className='login-input'
                    />
                    <button
                        type="button"
                        className="password-toggle-icon"
                        onClick={togglePasswordVisibility}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                        {!showPassword ? <EyeSlashIcon /> : <EyeIcon />}
                    </button>
                </div>

                {isSigningUp && (
                    <>
                        <label htmlFor="confirm-password">Re-enter Password</label>
                        <div className="password-input-container">
                            <input
                                type={showPassword ? 'text' : 'password'}
                                value={confirmPassword}
                                id="confirm-password"
                                name="confirm-password"
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="Re-enter password"
                                autoComplete="new-password"
                                required
                                className='login-input'
                            />
                            <button
                                type="button"
                                className="password-toggle-icon"
                                onClick={togglePasswordVisibility}
                                aria-label={showPassword ? "Hide password" : "Show password"}
                            >
                                {!showPassword ? <EyeSlashIcon /> : <EyeIcon />}
                            </button>
                        </div>
                    </>
                )}

                <button type="submit" className="primary" style={{ width: "100%" }}>
                    {isSigningUp ? 'Get started for free' : 'Login'}
                </button>
            </form>

            <div className="toggle-auth">
                {isSigningUp ? (
                    <p>
                        Already have an account?{' '}
                        <button type="button" className="auth-switch-link" onClick={() => setIsSigningUp(false)}>
                            Sign in
                        </button>
                    </p>
                ) : (
                    <p>
                        Don&apos;t have an account?{' '}
                        <button type="button" className="auth-switch-link" onClick={() => setIsSigningUp(true)}>
                            Create an account
                        </button>
                    </p>
                )}
            </div>
        </div>
    );
}
